import { Component, EventEmitter, Input, Output } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators, AbstractControl, ValidationErrors, AbstractControlOptions } from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';
import { RecaptchaService } from '../../services/recaptcha.service';
import { SignupService } from '../../services/signup.service';
import { UserService, User } from '../../services/user.service';

type UserFormMode = 'create' | 'edit';
export type UserFormSaveResult = 'success' | 'error';

@Component({
    selector: 'app-user-form',
    templateUrl: './user-form.component.html',
    styleUrls: ['./user-form.component.sass'],
    standalone: false
})
export class UserFormComponent {
  @Input() mode: UserFormMode = 'create';
  @Input() showTermsAcceptance = false;
  @Output() saveResult = new EventEmitter<UserFormSaveResult>();

  form!: UntypedFormGroup;
  isSaving = false;

  constructor(
    private fb: UntypedFormBuilder,
    private recaptchaService: RecaptchaService,
    private signupService: SignupService,
    private userService: UserService,
    private snackBar: MatSnackBar
  ) {}

  async ngOnInit() {
    const isCreate = this.mode === 'create';
    const options: AbstractControlOptions = { validators: this.passwordsMatchValidator };

    this.form = this.fb.group({
      name: ['', [Validators.required, Validators.minLength(2)]],
      email: [{ value: '', disabled: !isCreate }, [Validators.required, Validators.email]],
      password: ['', isCreate ? [Validators.required] : []],
      confirmPassword: ['', isCreate ? [Validators.required] : []]
    }, options);

    if (this.showTermsAcceptance) {
      this.form.addControl('acceptedTermsOfUse', this.fb.control(false, [Validators.requiredTrue]));
      this.form.addControl('acceptedPrivacyPolicy', this.fb.control(false, [Validators.requiredTrue]));
    }

    if (this.mode === 'edit') {
      const current = await this.userService.getCurrentUser();
      if (current) {
        this.form.patchValue({
          name: current.name,
          email: current.username
        });
      }
    }
  }

  // Group-level validator: when password is provided, it must equal confirmPassword
  private passwordsMatchValidator(group: AbstractControl): ValidationErrors | null {
    const password = group.get('password')?.value;
    const confirm = group.get('confirmPassword')?.value;
    if (!password && !confirm) return null; // both empty: ok in edit mode
    return password === confirm ? null : { passwordsMismatch: true };
  }

  // Returns false (and shows a snackbar) when terms acceptance is required but missing.
  private validateTermsAcceptance(): boolean {
    if (!this.showTermsAcceptance) {
      return true;
    }
    const acceptedTerms = this.form.get('acceptedTermsOfUse')?.value === true;
    const acceptedPrivacy = this.form.get('acceptedPrivacyPolicy')?.value === true;
    if (!acceptedTerms && !acceptedPrivacy) {
      this.snackBar.open('Você precisa aceitar os termos de uso e a política de privacidade para continuar.', 'OK', { duration: 3000 });
      return false;
    }
    if (!acceptedTerms) {
      this.snackBar.open('Você precisa aceitar os termos de uso para continuar.', 'OK', { duration: 3000 });
      return false;
    }
    if (!acceptedPrivacy) {
      this.snackBar.open('Você precisa aceitar a política de privacidade para continuar.', 'OK', { duration: 3000 });
      return false;
    }
    return true;
  }

  public async save(): Promise<void> {
    if (!this.validateTermsAcceptance()) {
      this.form.markAllAsTouched();
      return;
    }
    if (this.form.hasError('passwordsMismatch')) {
      this.snackBar.open('As senhas não coincidem', 'OK', { duration: 3000 });
      this.form.markAllAsTouched();
      return;
    }
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }
    this.isSaving = true;

    const { name, email, password } = this.form.value as { name: string; email: string; password?: string };

    if (this.mode === 'create') {
      let recaptchaToken = '';
      try {
        recaptchaToken = await this.recaptchaService.generateToken('SIGNUP');
      } catch {
        this.isSaving = false;
        this.snackBar.open('Houve um erro na validação de usuário. Tente novamente mais tarde.', 'OK', { duration: 3000 });
        return;
      }

      const signupRequest = { username: email, password: password || '', name } as {
        username: string;
        password: string;
        name: string;
        acceptedTermsOfUse?: boolean;
        acceptedPrivacyPolicy?: boolean;
      };
      if (this.showTermsAcceptance) {
        signupRequest.acceptedTermsOfUse = this.form.get('acceptedTermsOfUse')?.value === true;
        signupRequest.acceptedPrivacyPolicy = this.form.get('acceptedPrivacyPolicy')?.value === true;
      }

      this.signupService.signup(signupRequest, recaptchaToken).subscribe({
        next: () => {
          this.saveResult.emit('success');
        },
        error: () => {
          this.isSaving = false;
          this.saveResult.emit('error');
        },
        complete: () => {
          this.isSaving = false;
        }
      });
    } else {
      const patch: { name?: string; password?: string } = { name };
      if (password) patch.password = password;
      this.userService.updateUser(patch).subscribe({
        next: (user: User) => {
          this.snackBar.open('Perfil atualizado com sucesso', 'OK', { duration: 3000 });
          this.saveResult.emit('success');
        },
        error: () => {
          this.isSaving = false;
          this.snackBar.open('Falha ao atualizar perfil', 'OK', { duration: 3000 });
          this.saveResult.emit('error');
        },
        complete: () => {
          this.isSaving = false;
        }
      });
    }
  }
}


